import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Skeleton,
  Chip,
  Tooltip,
  Paper,
  Grid,
  Popover,
  TextField,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  format,
  addMonths,
  subMonths,
  startOfDay,
  getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import { durchfuehrungszeitraeumeService } from '../services/durchfuehrungszeitraeumeService';
import { auditsService } from '../services/auditsService';
import { kalenderService } from '../services/kalenderService';
import { Durchfuehrungszeitraum, AuditList } from '../types';

type EventType = 'zeitraum' | 'preflight';

interface CalendarEvent {
  id: string;
  type: EventType;
  auditId: number;
  auditName: string;
  auditStatus: string;
  startdatum: Date;
  enddatum: Date;
  beschreibung?: string;
  team: string[];
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Geplant':
      return '#42a5f5';
    case 'In Durchfuehrung':
      return '#ffa726';
    case 'Abgeschlossen':
    case 'Beendet':
      return '#3dd47e';
    case 'Abgebrochen':
      return '#ef5350';
    default:
      return '#8899aa';
  }
};

const isMultiDayEvent = (ev: CalendarEvent): boolean => {
  return startOfDay(ev.startdatum).getTime() !== startOfDay(ev.enddatum).getTime();
};

interface EventPlacement {
  event: CalendarEvent;
  startCol: number;
  endCol: number;
  lane: number;
  isStart: boolean;
  isEnd: boolean;
}

const getGlobalLaneAssignments = (allEvents: CalendarEvent[]): Map<string, number> => {
  const multiDayEvents = allEvents.filter(isMultiDayEvent);

  multiDayEvents.sort((a, b) => {
    const aDuration = startOfDay(a.enddatum).getTime() - startOfDay(a.startdatum).getTime();
    const bDuration = startOfDay(b.enddatum).getTime() - startOfDay(b.startdatum).getTime();
    if (bDuration !== aDuration) return bDuration - aDuration;
    return startOfDay(a.startdatum).getTime() - startOfDay(b.startdatum).getTime();
  });

  const assignments = new Map<string, number>();
  const laneOccupancy: { start: number; end: number }[][] = [];

  for (const event of multiDayEvents) {
    const evStart = startOfDay(event.startdatum).getTime();
    const evEnd = startOfDay(event.enddatum).getTime();

    let lane = 0;
    while (lane < 50) {
      if (!laneOccupancy[lane]) laneOccupancy[lane] = [];
      const hasConflict = laneOccupancy[lane].some(
        (range) => evStart <= range.end && evEnd >= range.start
      );
      if (!hasConflict) break;
      lane++;
    }

    if (!laneOccupancy[lane]) laneOccupancy[lane] = [];
    laneOccupancy[lane].push({ start: evStart, end: evEnd });
    assignments.set(event.id, lane);
  }

  return assignments;
};

const getWeekEventPlacements = (
  week: Date[],
  allEvents: CalendarEvent[],
  laneMap: Map<string, number>
): EventPlacement[] => {
  const weekStartTime = startOfDay(week[0]).getTime();
  const weekEndTime = startOfDay(week[6]).getTime();

  const overlapping = allEvents.filter((ev) => {
    if (!isMultiDayEvent(ev)) return false;
    const evStartTime = startOfDay(ev.startdatum).getTime();
    const evEndTime = startOfDay(ev.enddatum).getTime();
    return evStartTime <= weekEndTime && evEndTime >= weekStartTime;
  });

  const placements: EventPlacement[] = [];

  for (const event of overlapping) {
    const evStartTime = startOfDay(event.startdatum).getTime();
    const evEndTime = startOfDay(event.enddatum).getTime();

    let startCol = 0;
    if (evStartTime > weekStartTime) {
      const idx = week.findIndex((d) => startOfDay(d).getTime() === evStartTime);
      startCol = idx >= 0 ? idx : 0;
    }

    let endCol = 6;
    if (evEndTime < weekEndTime) {
      const idx = week.findIndex((d) => startOfDay(d).getTime() === evEndTime);
      endCol = idx >= 0 ? idx : 6;
    }

    const isStart = evStartTime >= weekStartTime;
    const isEnd = evEndTime <= weekEndTime;
    const lane = laneMap.get(event.id) ?? 0;

    placements.push({ event, startCol, endCol, lane, isStart, isEnd });
  }

  return placements;
};

const renderEventTooltip = (ev: CalendarEvent) => {
  const isPreflight = ev.type === 'preflight';
  return (
    <Box>
      {isPreflight && (
        <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#ce93d8', mb: 0.3 }}>
          PREFLIGHT-CHECK
        </Typography>
      )}
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
        {ev.auditName}
      </Typography>
      {ev.beschreibung && !isPreflight && (
        <Typography sx={{ fontSize: '0.7rem', mt: 0.3 }}>
          {ev.beschreibung}
        </Typography>
      )}
      <Typography sx={{ fontSize: '0.7rem', mt: 0.3, color: '#ccc' }}>
        {isPreflight
          ? format(ev.startdatum, 'dd.MM.yyyy')
          : `${format(ev.startdatum, 'dd.MM.yyyy')} – ${format(ev.enddatum, 'dd.MM.yyyy')}`}
      </Typography>
      {ev.team.length > 0 && (
        <Typography sx={{ fontSize: '0.7rem', mt: 0.5, color: '#aab' }}>
          Team: {ev.team.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

interface CalendarPageProps {
  hideHeader?: boolean;
}

const CalendarPage: React.FC<CalendarPageProps> = ({ hideHeader = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  /* Theme-aware Farben */
  const cal = {
    paperBg: isDark ? '#212744' : '#ffffff',
    border: isDark ? '#2e3560' : '#dfe3e8',
    headerBg: isDark ? '#2c3358' : '#f5f7fa',
    textPrimary: isDark ? '#e8edf3' : '#1a2332',
    textSecondary: isDark ? '#8899aa' : '#5f7082',
    textMuted: isDark ? '#556677' : '#8899aa',
    accent: isDark ? '#3dd47e' : '#2ba862',
    todayBg: isDark ? '#2c335880' : 'rgba(43,168,98,0.08)',
    outOfMonthBg: isDark ? '#181c3410' : 'rgba(0,0,0,0.02)',
    hoverBg: isDark ? '#2c335840' : 'rgba(0,0,0,0.04)',
    todayHighlight: isDark ? '#3dd47e20' : 'rgba(43,168,98,0.12)',
  };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [pickerYear, setPickerYear] = useState(currentDate.getFullYear());
  const [icalAnchor, setIcalAnchor] = useState<HTMLElement | null>(null);
  const [icalUrl, setIcalUrl] = useState<string | null>(null);
  const [icalLoading, setIcalLoading] = useState(false);

  const toggleStatus = (statusKey: string) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusKey)) {
        next.delete(statusKey);
      } else {
        next.add(statusKey);
      }
      return next;
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [zeitraeume, audits] = await Promise.all([
          durchfuehrungszeitraeumeService.list(),
          auditsService.list(),
        ]);

        const auditMap = new Map<number, AuditList>();
        audits.forEach((a) => auditMap.set(a.id, a));

        // Durchführungszeiträume als Events
        const zeitraumEvents: CalendarEvent[] = zeitraeume.map((zr) => {
          const audit = auditMap.get(zr.audit_id);
          return {
            id: `zr-${zr.id}`,
            type: 'zeitraum' as EventType,
            auditId: zr.audit_id,
            auditName: audit?.name || `Audit #${zr.audit_id}`,
            auditStatus: audit?.audit_status?.name || 'Unbekannt',
            startdatum: new Date(zr.startdatum),
            enddatum: new Date(zr.enddatum),
            beschreibung: zr.beschreibung,
            team: zr.mitarbeiter_team.map((m) => `${m.vorname} ${m.nachname}`),
          };
        });

        // Preflight-Checks als separate Events
        const preflightEvents: CalendarEvent[] = audits
          .filter((a) => a.preflight_datum)
          .map((a) => {
            const pfDate = new Date(a.preflight_datum!);
            return {
              id: `pf-${a.id}`,
              type: 'preflight' as EventType,
              auditId: a.id,
              auditName: a.name,
              auditStatus: a.audit_status?.name || 'Kein Status',
              startdatum: pfDate,
              enddatum: pfDate,
              beschreibung: 'Preflight-Check',
              team: [],
            };
          });

        setEvents([...zeitraumEvents, ...preflightEvents]);
      } catch (error) {
        console.error('Fehler beim Laden der Kalenderdaten:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredEvents = useMemo(
    () =>
      events.filter((ev) => {
        if (ev.type === 'preflight') return !hiddenStatuses.has('Preflight');
        return !hiddenStatuses.has(ev.auditStatus);
      }),
    [events, hiddenStatuses]
  );

  const globalLaneMap = useMemo(
    () => getGlobalLaneAssignments(filteredEvents),
    [filteredEvents]
  );

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentDate]);

  const getEventsForDay = (day: Date): CalendarEvent[] => {
    const dayStart = startOfDay(day);
    return filteredEvents.filter((ev) => {
      const evStart = startOfDay(ev.startdatum);
      const evEnd = startOfDay(ev.enddatum);
      return dayStart >= evStart && dayStart <= evEnd;
    });
  };

  const handlePrevMonth = () => setCurrentDate((d) => subMonths(d, 1));
  const handleNextMonth = () => setCurrentDate((d) => addMonths(d, 1));
  const handleToday = () => setCurrentDate(new Date());

  // Group days into weeks (rows of 7)
  const weeks: Date[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={150} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2 }} />
        <Grid container spacing={0}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Grid item xs key={`h-${i}`}>
              <Skeleton variant="text" height={32} sx={{ mx: 0.5 }} />
            </Grid>
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <Grid item xs key={`c-${i}`}>
              <Skeleton variant="rounded" height={80} sx={{ m: 0.5 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {!hideHeader && <PageHeader title="Kalender" />}

      {/* ── Navigation Header ──────────────────────────────────────── */}
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          px: 3,
          py: 1.5,
          mb: 2,
          borderRadius: 3,
          bgcolor: cal.paperBg,
          border: `1px solid ${cal.border}`,
        }}
      >
        {/* Heute-Button (absolut links) */}
        <Button
          variant="outlined"
          startIcon={<TodayIcon />}
          onClick={handleToday}
          size="small"
          sx={{
            position: 'absolute',
            left: 16,
            borderColor: cal.border,
            color: cal.textSecondary,
            '&:hover': { borderColor: cal.accent, color: cal.accent },
          }}
        >
          Heute
        </Button>

        {/* iCal-Export (absolut rechts) */}
        <Tooltip title="iCal-URL für Google Calendar & Co.">
          <IconButton
            onClick={async (e) => {
              setIcalAnchor(e.currentTarget);
              if (!icalUrl) {
                setIcalLoading(true);
                try {
                  const { url } = await kalenderService.getIcalUrl();
                  setIcalUrl(url);
                } finally {
                  setIcalLoading(false);
                }
              }
            }}
            sx={{
              position: 'absolute',
              right: 16,
              color: cal.textSecondary,
              '&:hover': { color: cal.accent },
            }}
          >
            <CalendarMonthIcon />
          </IconButton>
        </Tooltip>

        {/* Zentrierte Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={handlePrevMonth} sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="h5"
            onClick={(e) => {
              setPickerYear(currentDate.getFullYear());
              setPickerAnchor(e.currentTarget);
            }}
            sx={{
              fontWeight: 700,
              color: cal.textPrimary,
              minWidth: 220,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              px: 1.5,
              py: 0.3,
              transition: 'background-color 0.15s',
              '&:hover': {
                bgcolor: isDark ? '#ffffff10' : '#00000008',
              },
            }}
          >
            {format(currentDate, 'MMMM yyyy', { locale: de })}
          </Typography>
          <IconButton onClick={handleNextMonth} sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* ── Monat/Jahr-Picker Popover ──────────────────────────────── */}
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              bgcolor: cal.paperBg,
              border: `1px solid ${cal.border}`,
              p: 2,
              minWidth: 280,
            },
          },
        }}
      >
        {/* Year navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setPickerYear((y) => y - 1)}
            sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              color: cal.textPrimary,
              minWidth: 60,
              textAlign: 'center',
            }}
          >
            {pickerYear}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPickerYear((y) => y + 1)}
            sx={{ color: cal.textSecondary, '&:hover': { color: cal.accent } }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Month grid (4x3) */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.5,
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => {
            const isCurrentMonth =
              pickerYear === currentDate.getFullYear() && i === currentDate.getMonth();
            const isThisMonth =
              pickerYear === new Date().getFullYear() && i === new Date().getMonth();
            const monthLabel = format(new Date(pickerYear, i, 1), 'MMM', { locale: de });

            return (
              <Box
                key={i}
                onClick={() => {
                  setCurrentDate(new Date(pickerYear, i, 1));
                  setPickerAnchor(null);
                }}
                sx={{
                  py: 1,
                  px: 0.5,
                  textAlign: 'center',
                  borderRadius: 2,
                  cursor: 'pointer',
                  fontWeight: isCurrentMonth ? 700 : 500,
                  fontSize: '0.85rem',
                  color: isCurrentMonth
                    ? cal.accent
                    : isThisMonth
                    ? cal.textPrimary
                    : cal.textSecondary,
                  bgcolor: isCurrentMonth
                    ? `${cal.accent}20`
                    : 'transparent',
                  border: isThisMonth && !isCurrentMonth
                    ? `1px solid ${cal.border}`
                    : '1px solid transparent',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: isCurrentMonth
                      ? `${cal.accent}30`
                      : isDark
                      ? '#ffffff10'
                      : '#00000008',
                  },
                }}
              >
                {monthLabel}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {/* ── iCal-URL Popover ────────────────────────────────────── */}
      <Popover
        open={Boolean(icalAnchor)}
        anchorEl={icalAnchor}
        onClose={() => setIcalAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              bgcolor: cal.paperBg,
              border: `1px solid ${cal.border}`,
              p: 2,
              minWidth: 420,
            },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: cal.textPrimary }}>
          Kalender abonnieren (iCal)
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, color: cal.textSecondary }}>
          Diese URL in Google Calendar oder anderen Kalender-Apps hinzufügen, um Ihre Audit-Termine automatisch zu
          synchronisieren.
        </Typography>
        {icalLoading ? (
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
        ) : icalUrl ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={icalUrl}
              inputProps={{ readOnly: true }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' },
              }}
            />
            <Tooltip title="URL kopieren">
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(icalUrl);
                }}
                sx={{ color: cal.accent }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Popover>

      {/* ── Legende (klickbar als Filter) ────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Geplant', color: '#42a5f5', statusKey: 'Geplant' },
          { label: 'In Durchführung', color: '#ffa726', statusKey: 'In Durchfuehrung' },
          { label: 'Abgeschlossen', color: '#3dd47e', statusKey: 'Abgeschlossen' },
          { label: 'Abgebrochen', color: '#ef5350', statusKey: 'Abgebrochen' },
          { label: 'Preflight-Check', color: '#ce93d8', statusKey: 'Preflight' },
        ].map((item) => {
          const isHidden = hiddenStatuses.has(item.statusKey);
          return (
            <Box
              key={item.label}
              onClick={() => toggleStatus(item.statusKey)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                opacity: isHidden ? 0.35 : 1,
                transition: 'opacity 0.2s',
                userSelect: 'none',
                '&:hover': { opacity: isHidden ? 0.5 : 0.8 },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: item.label === 'Preflight-Check' ? 2 : '50%',
                  bgcolor: item.color,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: cal.textSecondary,
                  fontSize: '0.75rem',
                  textDecoration: isHidden ? 'line-through' : 'none',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Calendar Grid ──────────────────────────────────────────── */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${cal.border}`,
          bgcolor: cal.paperBg,
        }}
      >
        {/* Weekday header */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: cal.headerBg,
            borderBottom: `1px solid ${cal.border}`,
          }}
        >
          {/* KW placeholder */}
          <Box
            sx={{
              width: 44,
              flexShrink: 0,
              borderRight: `1px solid ${cal.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: cal.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
            >
              KW
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            }}
          >
            {WEEKDAYS.map((day) => (
              <Box
                key={day}
                sx={{
                  py: 1.2,
                  textAlign: 'center',
                  borderRight: `1px solid ${cal.border}`,
                  '&:last-child': { borderRight: 'none' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: cal.accent,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Day cells */}
        {weeks.map((week, weekIdx) => {
          const placements = getWeekEventPlacements(week, filteredEvents, globalLaneMap);
          const maxLane = placements.length > 0 ? Math.max(...placements.map((p) => p.lane)) : -1;
          const barAreaHeight = (maxLane + 1) * 22;

          return (
            <Box
              key={weekIdx}
              sx={{
                display: 'flex',
                borderBottom: weekIdx < weeks.length - 1 ? `1px solid ${cal.border}` : 'none',
              }}
            >
              {/* KW label */}
              <Box
                sx={{
                  width: 44,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  pt: 1,
                  borderRight: `1px solid ${cal.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: cal.textMuted,
                    lineHeight: 1,
                  }}
                >
                  {getISOWeek(week[0])}
                </Typography>
              </Box>

              {/* Grid container */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                }}
              >
                {/* Day cells */}
                {week.map((day, dayIdx) => {
                  const dayEvents = getEventsForDay(day);
                  const singleDayEvents = dayEvents.filter((ev) => !isMultiDayEvent(ev));
                  const inMonth = isSameMonth(day, currentDate);
                  const today = isToday(day);

                  return (
                    <Box
                      key={dayIdx}
                      sx={{
                        minHeight: 110 + barAreaHeight,
                        minWidth: 0,
                        overflow: 'hidden',
                        borderRight: dayIdx < 6 ? `1px solid ${cal.border}` : 'none',
                        bgcolor: today
                          ? cal.todayBg
                          : !inMonth
                          ? cal.outOfMonthBg
                          : 'transparent',
                        opacity: inMonth ? 1 : 0.4,
                        transition: 'background-color 0.15s',
                        '&:hover': {
                          bgcolor: inMonth ? cal.hoverBg : undefined,
                        },
                      }}
                    >
                      {/* Day number */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.8, pb: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: today ? 800 : 500,
                            color: today ? cal.accent : inMonth ? cal.textPrimary : cal.textMuted,
                            width: 26,
                            height: 26,
                            lineHeight: '26px',
                            textAlign: 'center',
                            borderRadius: '50%',
                            bgcolor: today ? cal.todayHighlight : 'transparent',
                          }}
                        >
                          {format(day, 'd')}
                        </Typography>
                      </Box>

                      {/* Space reserved for multi-day bar area */}
                      {barAreaHeight > 0 && <Box sx={{ height: barAreaHeight + 6, flexShrink: 0 }} />}

                      {/* Single-day event chips */}
                      <Box sx={{ px: 0.8, pb: 0.8, display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.3 }}>
                        {singleDayEvents.slice(0, 3).map((ev) => {
                          const isPreflight = ev.type === 'preflight';
                          const chipColor = isPreflight ? '#ce93d8' : getStatusColor(ev.auditStatus);
                          const chipLabel = isPreflight ? `PF: ${ev.auditName}` : ev.auditName;

                          return (
                            <Tooltip key={ev.id} title={renderEventTooltip(ev)} arrow>
                              <Chip
                                label={chipLabel}
                                size="small"
                                {...navigableProps(navigate, `/audits/${ev.auditId}`)}
                                sx={{
                                  height: 20,
                                  maxWidth: '100%',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: `${chipColor}20`,
                                  color: chipColor,
                                  border: `1px solid ${chipColor}40`,
                                  borderStyle: isPreflight ? 'dashed' : 'solid',
                                  cursor: 'pointer',
                                  '& .MuiChip-label': {
                                    px: 0.8,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  },
                                  '&:hover': {
                                    bgcolor: `${chipColor}35`,
                                  },
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                        {singleDayEvents.length > 3 && (
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: cal.textSecondary,
                              textAlign: 'center',
                              fontWeight: 600,
                            }}
                          >
                            +{singleDayEvents.length - 3} weitere
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {/* Multi-day event bars (absolutely positioned over grid) */}
                {placements.map((placement) => {
                  const { event, startCol, endCol, lane, isStart, isEnd } = placement;
                  const barColor = getStatusColor(event.auditStatus);
                  const isHovered = hoveredEventId === event.id;
                  const isInProgress = event.auditStatus === 'In Durchfuehrung';

                  // Progress calculation relative to THIS bar segment
                  let segProgress = 0;
                  if (isInProgress) {
                    const todayTime = startOfDay(new Date()).getTime();
                    const segStartTime = startOfDay(week[startCol]).getTime();
                    const segEndTime = startOfDay(week[endCol]).getTime() + 86400000;
                    if (todayTime >= segEndTime) {
                      segProgress = 1;
                    } else if (todayTime > segStartTime) {
                      segProgress = (todayTime - segStartTime) / (segEndTime - segStartTime);
                    }
                  }

                  return (
                    <Tooltip key={`bar-${event.id}-w${weekIdx}`} title={renderEventTooltip(event)} arrow>
                      <Box
                        {...navigableProps(navigate, `/audits/${event.auditId}`)}
                        onMouseEnter={() => setHoveredEventId(event.id)}
                        onMouseLeave={() => setHoveredEventId(null)}
                        sx={{
                          position: 'absolute',
                          top: 34 + lane * 22,
                          left: `calc(${(startCol / 7) * 100}% + ${isStart ? 4 : 0}px)`,
                          width: `calc(${((endCol - startCol + 1) / 7) * 100}% - ${(isStart ? 4 : 0) + (isEnd ? 4 : 0)}px)`,
                          height: 20,
                          zIndex: isHovered ? 3 : 2,
                          cursor: 'pointer',
                          bgcolor: isHovered ? `${barColor}55` : `${barColor}30`,
                          borderLeft: isStart ? `3px solid ${barColor}` : 'none',
                          borderRight: isEnd ? `3px solid ${barColor}` : 'none',
                          borderTop: isHovered ? `1px solid ${barColor}90` : '1px solid transparent',
                          borderBottom: isHovered ? `1px solid ${barColor}90` : '1px solid transparent',
                          borderRadius:
                            isStart && isEnd
                              ? '4px'
                              : isStart
                              ? '4px 0 0 4px'
                              : isEnd
                              ? '0 4px 4px 0'
                              : 0,
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
                          boxShadow: isHovered ? `0 1px 6px ${barColor}40` : 'none',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: barColor,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            px: 0.8,
                            zIndex: 1,
                          }}
                        >
                          {event.auditName}
                        </Typography>

                        {/* Progress indicator for "In Durchfuehrung" */}
                        {isInProgress && segProgress > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              height: 2,
                              width: `${segProgress * 100}%`,
                              bgcolor: barColor,
                              opacity: 0.8,
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default CalendarPage;
